import Buttons, { BottomSheetButtonSet } from './Buttons/Buttons';
import React, { useEffect, useRef, useState } from 'react';

import IconCommon from '@/components/Common/DesignSystem/Icon/IconCommon';
import Modal from 'react-modal';
import ResizeObserver from '../../ResizeObserver/ResizeObserver';
import { ScrollStore } from '@/stores/ScrollStore';
import clsx from 'clsx';
import { debounce } from '@/utils';
import { nothing } from '@/utils/yambda';
import { observer } from 'mobx-react-lite';
import { pipe } from 'ramda';
import styles from './BottomSheet.module.scss';

export interface IBottomSheetProps {
  children: {
    body: React.ReactNode;
    buttons?: BottomSheetButtonSet;
    title?: string | React.ReactNode;
  };
  isOpen: boolean;
  onRequestClose: () => void;
  onAfterClose?: () => void;
  /** BottomSheet의 body에 ref를 설정하기 위한 값 */
  bottomSheetBodyRef?: React.MutableRefObject<HTMLElement | null>;
  /** 글로벌 필터의 UI 분기처리를 위한 값 */
  isGlobalFilter?: boolean;
  // overlay 위치는 동적으로 계산되기 때문에 레이아웃 시프팅 발생 방지를 위한 디폴트 값
  defaultOverlayBottom?: number;
}

const BottomSheet: React.FC<IBottomSheetProps> = ({
  children: { body, buttons, title },
  isOpen,
  onRequestClose,
  onAfterClose = nothing,
  bottomSheetBodyRef,
  isGlobalFilter,
  defaultOverlayBottom = 0,
}) => {
  const [isMounted, setMounted] = useState(false);
  const [isScrollable, setScrollable] = useState(false);
  const [overlayBottom, setOverlayBottom] = useState(defaultOverlayBottom);
  const scrollStore = useRef(new ScrollStore()).current;
  const scrollRootRef = useRef<HTMLElement>();
  const scrollContentsHeight = useRef(Infinity);

  const calcOverlayBottom = (): void => {
    if (scrollRootRef.current) {
      setOverlayBottom(window.innerHeight - scrollRootRef.current.getBoundingClientRect().bottom);
    }
  };

  const mount = (el: HTMLDivElement): void => {
    if (el) {
      scrollRootRef.current = el;
      if (bottomSheetBodyRef) {
        bottomSheetBodyRef.current = el;
      }
      setMounted(true);
    }
  };

  const determineScrollability = (): void => {
    const scrollRootHeight = scrollRootRef.current?.getBoundingClientRect().height;
    setScrollable(!!(scrollRootHeight && scrollRootHeight < scrollContentsHeight.current));
    scrollStore.updateIsEdge(); // 리사이즈로 인해 스크롤 여부가 바뀌어도 isBottom이 true라서 그레이디언트 오버레이가 안생김!!
    window.setTimeout(calcOverlayBottom, 200);
  };

  const setScrollContentsHeight = (_: number, height: number): void => {
    scrollContentsHeight.current = height;
    determineScrollability();
  };

  useEffect(() => {
    const _determineScrollability = debounce(determineScrollability, 100);
    if (isMounted) {
      scrollStore.bindEvent(scrollRootRef);
      window.addEventListener('resize', _determineScrollability);
      _determineScrollability();
    }

    return () => {
      scrollStore.unbindEvent();
      window.removeEventListener('resize', _determineScrollability);
    };
  }, [isMounted]);

  return (
    <Modal
      className={{
        afterOpen: styles.containerAfterOpen,
        base: clsx(styles.container, isGlobalFilter && styles.globalFilter),
        beforeClose: styles.containerBeforeClose,
      }}
      bodyOpenClassName="ReactModal__Body-NoIE--open"
      overlayClassName={{
        afterOpen: styles.overlayAfterOpen,
        base: styles.overlay,
        beforeClose: styles.overlayBeforeClose,
      }}
      ariaHideApp={false}
      closeTimeoutMS={200}
      isOpen={isOpen}
      onAfterClose={pipe(onAfterClose, () => setMounted(false))}
      onRequestClose={onRequestClose}
    >
      <header className={styles.header}>
        <button className={styles.close} onClick={onRequestClose}>
          <IconCommon type="icon_close" />
        </button>
        {title && <h2 className={styles.heading}>{title}</h2>}
      </header>
      <section ref={mount} className={styles.body}>
        <div className={styles.scrollContents}>
          {body}
          <ResizeObserver onResize={setScrollContentsHeight} />
        </div>
      </section>
      <div
        className={clsx(styles.scrollOverlay, isGlobalFilter && styles.globalFilter)}
        style={{ bottom: `${overlayBottom}px`, opacity: isScrollable && !scrollStore.isBottom ? 1 : 0 }}
      />
      {buttons && <Buttons>{buttons}</Buttons>}
    </Modal>
  );
};

export default observer(BottomSheet);
